import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  AlertTriangle, FileWarning, Send, CheckCircle2, Clock, Plus, X,
  Upload, Loader2, ShieldCheck, FileCheck2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPI_DOC = [
  { value: 'bolletta', label: 'Bolletta ultima fornitura' },
  { value: 'documento_riconoscimento', label: "Carta d'identità / Patente" },
  { value: 'codice_fiscale', label: 'Codice Fiscale / Tessera sanitaria' },
  { value: 'iban', label: 'IBAN / Estratto Conto' },
  { value: 'altro', label: 'Altro documento' },
];

/**
 * Alert documenti mancanti su una pratica con upload inline.
 * - Mostra check documenti obbligatori (bolletta, doc riconoscimento) + contatti cliente
 * - Super_admin/back_office puo' richiedere documenti al sub-agente
 * - Sub-agente (e admin) possono caricare i documenti direttamente da ogni riga
 * - Tutti i file finiscono su Emergent Object Storage (persistenti)
 */
const ContractDocsAlert = ({ contractId, isAdmin, onDocRequested }) => {
  const [check, setCheck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [tipoDoc, setTipoDoc] = useState('bolletta');
  const [descrizione, setDescrizione] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  const fetchCheck = useCallback(async () => {
    if (!contractId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/contracts/${contractId}/documenti-check`);
      setCheck(res.data);
    } catch (e) {
      // ignore silently
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => { fetchCheck(); }, [fetchCheck]);

  const handleRichiedi = async () => {
    if (!tipoDoc) { toast.error('Seleziona un tipo documento'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/contracts/${contractId}/richiedi-documento`, {
        tipo: tipoDoc,
        descrizione: descrizione.trim() || undefined,
      });
      toast.success('Richiesta inviata al sub-agente');
      setShowForm(false);
      setDescrizione('');
      setTipoDoc('bolletta');
      await fetchCheck();
      onDocRequested?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nell\'invio della richiesta');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRisolvi = async (alertId) => {
    try {
      await axios.put(`${API_URL}/api/contracts/${contractId}/risolvi-documento/${alertId}`);
      toast.success('Alert risolto');
      await fetchCheck();
    } catch (e) {
      toast.error('Errore');
    }
  };

  const uploadFile = async (file, tipo, alertId, key) => {
    if (!file) return;
    setUploadingKey(key);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('tipo', tipo);
      if (alertId) form.append('alert_id', alertId);
      await axios.post(`${API_URL}/api/contracts/${contractId}/upload-documento`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Documento caricato con successo');
      await fetchCheck();
      onDocRequested?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore upload');
    } finally {
      setUploadingKey(null);
    }
  };

  if (loading || !check) return null;
  const total = check.total_missing || 0;
  const hasBlockers = total > 0;

  return (
    <Card
      className={hasBlockers ? 'border-red-300 bg-red-50/40' : 'border-emerald-200 bg-emerald-50/30'}
      data-testid="contract-docs-alert"
    >
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {hasBlockers ? (
            <AlertTriangle className="w-5 h-5 text-red-600" />
          ) : (
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          )}
          <span>Documenti Pratica</span>
          <span className="text-[10px] text-slate-500 flex items-center gap-1 ml-2 font-normal" title="I documenti sono conservati in modo sicuro e persistente">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
            Archivio protetto
          </span>
          {hasBlockers && (
            <Badge className="bg-red-500 text-white ml-auto" data-testid="docs-alert-count">
              {total} da completare
            </Badge>
          )}
          {!hasBlockers && (
            <Badge className="bg-emerald-500 text-white ml-auto">Completa</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Documenti obbligatori mancanti - con upload inline */}
        {check.missing_required?.length > 0 && (
          <div className="rounded-xl bg-white border border-red-200 p-3">
            <p className="text-xs font-semibold text-red-700 uppercase tracking-wider mb-2 flex items-center gap-1">
              <FileWarning className="w-3.5 h-3.5" /> Documenti obbligatori
            </p>
            <ul className="space-y-2">
              {check.missing_required.map((d) => (
                <li key={d.tipo} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                  <span className="flex-1 text-sm text-slate-700">{d.label}</span>
                  <InlineUpload
                    testId={`upload-required-${d.tipo}`}
                    isBusy={uploadingKey === `req-${d.tipo}`}
                    onFile={(f) => uploadFile(f, d.tipo, null, `req-${d.tipo}`)}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Contatti cliente mancanti (solo info, non ha upload) */}
        {check.missing_contatti?.length > 0 && (
          <div className="rounded-xl bg-white border border-amber-200 p-3">
            <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-2">
              Dati cliente mancanti
            </p>
            <ul className="space-y-1">
              {check.missing_contatti.map((c) => (
                <li key={c.campo} className="text-sm text-slate-700 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  {c.label}
                  <span className="text-[10px] text-slate-400 ml-auto">Aggiornare in anagrafica cliente</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Alert manuali (richieste specifiche) - con upload inline */}
        {check.alert_manuali?.length > 0 && (
          <div className="rounded-xl bg-white border border-orange-200 p-3">
            <p className="text-xs font-semibold text-orange-700 uppercase tracking-wider mb-2 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> Richieste specifiche
            </p>
            <ul className="space-y-2">
              {check.alert_manuali.map((a) => (
                <li key={a.id} className="flex items-start gap-2" data-testid={`alert-${a.id}`}>
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800">{a.label}</p>
                    {a.descrizione && <p className="text-xs text-slate-600 mt-0.5">{a.descrizione}</p>}
                    <p className="text-[10px] text-slate-400 mt-1">
                      Richiesto da {a.richiesto_da_nome} • {new Date(a.richiesto_at).toLocaleDateString('it-IT', {day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit'})}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <InlineUpload
                      testId={`upload-alert-${a.id}`}
                      isBusy={uploadingKey === `alert-${a.id}`}
                      onFile={(f) => uploadFile(f, a.tipo, a.id, `alert-${a.id}`)}
                    />
                    {isAdmin && (
                      <Button
                        type="button" size="sm" variant="ghost"
                        className="h-8 text-xs text-emerald-700 hover:bg-emerald-100"
                        onClick={() => handleRisolvi(a.id)}
                        data-testid={`risolvi-${a.id}`}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Risolvi
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Caricamento libero (sempre visibile a tutti) */}
        <div className="rounded-xl bg-white border border-slate-200 p-3">
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 flex items-center gap-1">
            <FileCheck2 className="w-3.5 h-3.5" /> Carica un documento
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {TIPI_DOC.map(t => (
              <InlineUpload
                key={t.value}
                variant="pill"
                label={t.label}
                testId={`upload-free-${t.value}`}
                isBusy={uploadingKey === `free-${t.value}`}
                onFile={(f) => uploadFile(f, t.value, null, `free-${t.value}`)}
              />
            ))}
          </div>
        </div>

        {/* Form richiesta documento (solo admin) */}
        {isAdmin && !showForm && (
          <Button
            type="button" size="sm" variant="outline"
            onClick={() => setShowForm(true)}
            className="w-full border-dashed"
            data-testid="richiedi-doc-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Richiedi documento al sub-agente
          </Button>
        )}
        {isAdmin && showForm && (
          <div className="rounded-xl bg-white border border-slate-200 p-3 space-y-3" data-testid="richiedi-doc-form">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-800">Nuova richiesta documento</p>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <Select value={tipoDoc} onValueChange={setTipoDoc}>
              <SelectTrigger data-testid="richiedi-doc-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIPI_DOC.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea
              placeholder="Descrizione (opzionale): specifica cosa serve, es. 'Carta di identità fronte e retro leggibile'"
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              rows={2}
              data-testid="richiedi-doc-descrizione"
            />
            <div className="flex gap-2">
              <Button
                type="button" size="sm"
                onClick={handleRichiedi}
                disabled={submitting}
                className="bg-sky-600 hover:bg-sky-700 text-white"
                data-testid="richiedi-doc-invia"
              >
                <Send className="w-3.5 h-3.5 mr-1" />
                {submitting ? 'Invio…' : 'Invia richiesta'}
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setShowForm(false)}>
                Annulla
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Small inline upload input. Two variants:
 * - default: compact icon button
 * - pill: full-width labeled tile
 */
const InlineUpload = ({ onFile, isBusy, testId, variant = 'default', label }) => {
  const inputRef = useRef(null);
  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file) onFile(file);
    e.target.value = '';
  };

  if (variant === 'pill') {
    return (
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={isBusy}
        className="flex items-center gap-2 px-3 py-2 border-2 border-dashed border-slate-200 rounded-xl hover:border-sky-400 hover:bg-sky-50/50 transition-colors text-left text-xs disabled:opacity-50"
        data-testid={testId}
      >
        {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin text-sky-500 shrink-0" /> : <Upload className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
        <span className="text-slate-700 truncate">{label}</span>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.odt,.ods,.txt,.rtf,.zip,.heic,.heif"
          className="hidden"
          onChange={handleChange}
        />
      </button>
    );
  }

  return (
    <>
      <Button
        type="button" size="sm"
        onClick={() => inputRef.current?.click()}
        disabled={isBusy}
        className="h-8 bg-sky-600 hover:bg-sky-700 text-white text-xs"
        data-testid={testId}
      >
        {isBusy ? (
          <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Carico…</>
        ) : (
          <><Upload className="w-3.5 h-3.5 mr-1" /> Carica</>
        )}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.odt,.ods,.txt,.rtf,.zip,.heic,.heif"
        className="hidden"
        onChange={handleChange}
      />
    </>
  );
};

export default ContractDocsAlert;
